/**
 * Transcript bubble ids: `bubble-<speaker token>-<creation ms>`.
 *
 * A bubble id travels further than the DOM. The Anderra chat request ships it
 * to the model (lib/anderraTranscriptBubbles.ts), the model cites it back as a
 * markdown link destination `[quote](#transcript:<id>)`, and the chat renders
 * that link. A CommonMark link destination cannot hold a space, and every
 * LiveKit identity here carries one (`Officer Kelly__off_8xlk`,
 * `Jane Doe__cit_xyz2`), so the raw identity made every citation render as
 * raw text (owner report 2026-09-23). The speaker part is therefore encoded
 * into an alphabet that is literal in a URL fragment, a markdown destination,
 * a CSS attribute selector and an HTML attribute: `[A-Za-z0-9_-]`.
 *
 * Encoding: each UTF-8 byte outside `[A-Za-z0-9-]` becomes `_` plus two
 * uppercase hex digits (so `_` itself is `_5F`). A lone UTF-16 surrogate has no
 * UTF-8 form (TextEncoder would turn every one into U+FFFD), so it becomes `_S`
 * plus its four hex digits. `_` is always followed by two hex digits or `S`, so
 * the scheme is prefix-free: two different identities never share a token.
 * The trailing `-<ms>` is kept because tooling orders rows by it
 * (scripts/analyze_live_semantic_fidelity.py).
 *
 * Ids persisted before this change keep their original form (a restored
 * transcript reads `bubble_id` back verbatim); the markdown renderer tolerates
 * those, see lib/markdown/renderSafeMarkdownHtml.ts.
 */

const LITERAL_BYTE = /^[A-Za-z0-9-]$/;

const encoder = new TextEncoder();

export function bubbleIdSpeakerToken(speakerId: string): string {
  let token = '';
  // for…of walks code points: a surrogate PAIR arrives whole, a lone one alone.
  for (const char of speakerId) {
    const code = char.codePointAt(0)!;
    if (code >= 0xd800 && code <= 0xdfff) {
      token += `_S${code.toString(16).toUpperCase()}`;
      continue;
    }
    for (const byte of encoder.encode(char)) {
      const ascii = String.fromCharCode(byte);
      token += byte < 0x80 && LITERAL_BYTE.test(ascii)
        ? ascii
        : `_${byte.toString(16).toUpperCase().padStart(2, '0')}`;
    }
  }
  return token;
}

export function transcriptBubbleId(speakerId: string, createdAtMs: number): string {
  return `bubble-${bubbleIdSpeakerToken(speakerId)}-${createdAtMs}`;
}
